import { cast, Instance, SnapshotOut, types } from 'mobx-state-tree'
import { HOW_TO_PLAY_MODULE_ID } from '@ppwr/poker'
import { extendLogger, LoggerContexts } from '../../utils/logger'
import {
  LearningModuleAndStatModel,
  LearningModuleAndStatModelType,
  ZogoCredentialModel,
  ZogoCredentialModelType
} from '../api-store'

import { withRootStore } from '../extensions'
import { useSessionStore } from '../session/useSessionStore'

const logger = extendLogger(LoggerContexts.ZogoStore)

/**
 * Zogo Store Model
 */
export const ZogoStore = types
  .model('ZogoStore')
  .props({
    zogoCredential: types.maybeNull(ZogoCredentialModel),
    learningModulesAndStats: types.optional(types.array(LearningModuleAndStatModel), []),
    lastTimeTokenFetched: types.maybe(types.Date),
    iframeViewed: types.optional(types.boolean, false)
  })
  .extend(withRootStore)
  .actions((self) => ({
    clearZogoToken() {
      self.zogoCredential = null
    },
    setZogoToken(zogoCredential: ZogoCredentialModelType) {
      self.zogoCredential = zogoCredential
      self.lastTimeTokenFetched = new Date()
    },
    setLearningModulesAndStats(data: LearningModuleAndStatModelType[]) {
      self.learningModulesAndStats = cast(data)
    },
    setIframeViewed() {
      self.iframeViewed = true
    }
  }))
  .views((self) => ({
    get howToPlayModule() {
      const howToPlayModule = self.learningModulesAndStats.find(
        (l) => l.learningModule?.moduleId === HOW_TO_PLAY_MODULE_ID
      )

      return howToPlayModule
    }
  }))
  .actions((self) => ({
    async getZogoToken(): Promise<ZogoCredentialModelType> {
      //TODO: improve zogo token expiration logi
      /*if (self.zogoCredential?.access_token && self.lastTimeTokenFetched) {
        const now = new Date()
        const diff = now.getTime() - self.lastTimeTokenFetched.getTime()
        const diffInSeconds = diff / 1000
        const expiresIn = self.zogoCredential.expires_in ?? 0

        // early return when token do not expire yet
        if (diffInSeconds < expiresIn) {
          logger.log('[zogo getZogoToken] zogo token did not expire yet')
          return self.zogoCredential
        }
      }*/

      try {
        const response = await self.rootStore.api.queryGetZogoToken(undefined, undefined, { fetchPolicy: 'no-cache' })
        console.debug('[zogo getZogoToken] getting new zogo token', response.getZogoToken)
        response && self.setZogoToken(response.getZogoToken)
        return response.getZogoToken
      } catch (error) {
        logger.error('Error in getting zogo token', error)
        throw error
      }
    }
  }))
  .actions((self) => ({
    async saveLearningModuleStats(moduleId: number): Promise<boolean> {
      if (Number.isNaN(moduleId)) {
        logger.error('Error while saving learning module stats, moduleId is NaN')
        return false
      }
      const userId = self.rootStore.session.user?._id
      if (userId) {
        try {
          const response = await self.rootStore.api.mutateSaveLearningModuleStats({ moduleId, userId })
          return Boolean(response)
        } catch (error) {
          logger.error('Error while saving learning module stats', error)
        }
      }
      return false
    }
  }))
  .actions((self) => ({
    async getLearningModulesAndStats() {
      const userId = self.rootStore.session.user?._id
      if (userId) {
        logger.debug('zogo getlearningmodulesAndStats calling api')
        const response = await self.rootStore.api.queryGetLearningModulesAndStats(
          { userId },
          'learningModule{_id, moduleId, moduleTitle, moduleImageUrl, categoryId, categoryName, topicId, topicName} stats{accuracy, completedTimes, moduleCompletedTime}',
          {
            fetchPolicy: 'no-cache'
          }
        )
        self.setLearningModulesAndStats(response.getLearningModulesAndStats)
      }
    }
  }))
  .actions((self) => {
    const afterAttach = async () => {
      if (useSessionStore.getState().isLoggedIn()) {
        await self.getZogoToken()
      }
    }
    return { afterAttach }
  })

/**
 * The ZogoStore instance.
 */
export type ZogoStoreType = Instance<typeof ZogoStore>

/**
 * The data of a ZogoStore.
 */
export type ZogoStoreSnapshot = SnapshotOut<typeof ZogoStore>
